import { useEffect } from 'react';

/**
 * Refetch a board's data whenever the user comes back to the tab or refocuses
 * the window. Used by every project board (Projects, ClosedProjects,
 * AccountsReceivable, DeadProjects) so a PM that edits a project elsewhere
 * never lands on a stale list.
 *
 *   useBoardAutoRefresh(fetchProjects);
 *
 * The supplied `fetchFn` should accept an `{ silent: true }` option so it
 * can refetch without flashing the loading spinner.
 */
export function useBoardAutoRefresh(fetchFn) {
  useEffect(() => {
    if (typeof fetchFn !== 'function') return undefined;
    const onVisible = () => { if (!document.hidden) fetchFn({ silent: true }); };
    const onFocus = () => fetchFn({ silent: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
    // We intentionally re-bind whenever fetchFn changes so callers can
    // close over fresh state without us having to memoise the function.
  }, [fetchFn]);
}

export default useBoardAutoRefresh;
